import {
  BODY_SEGMENT_KINDS,
  DEFAULT_BODY_SEGMENT_DURATION,
  DEFAULT_OVERLAY_DURATION,
  DEFAULT_SEGMENT_DURATION,
  MILLISECONDS_PER_PIXEL,
  OPEN_TEASE_AND_CREDITS_KINDS,
  RESIZE_HANDLE_WIDTH,
  SEGMENTS_GAP
} from '@/components/Packaging/constants';
import { isAssignedOverlay } from '@/components/Packaging/typeGuards';
import { setPlayerCurrentFrame } from '@/redux/slices/packagingSlice';
import {
  AssemblyInputsDataRequest,
  AssemblyInputsResponse,
  AssemblyTemplate,
  AssemblyTemplateBERequest,
  AssemblyTemplateBEResponse,
  DRAGGABLE_TYPES,
  DraggableId,
  FrameRate,
  Frames,
  Milliseconds,
  OVERLAY_KIND,
  OverlayItem,
  OverlayItemBERequest,
  OverlayItemBEResponse,
  OverlayItemUnassigned,
  OverlayLayer,
  OverlayLayerBERequest,
  Pixels,
  Segment,
  SEGMENT_KIND,
  SegmentItem,
  SegmentItemBERequest,
  SegmentItemBEResponse,
  Source,
  SourceBERequest,
  SourceBEResponse
} from '@/redux/slices/packagingSlice.types';
import { AppDispatch } from '@/redux/store';
import { SeekMode, TimeFormat } from '@accurate-player/accurate-player-core';
import type { SegmentService as PlayerAPI } from '@accurate-player/accurate-player-segment';
import { nanoid } from 'nanoid';
import Timecode from 'smpte-timecode';

interface Options {
  hideZeroHours?: boolean;
  showMilliseconds?: boolean;
}

export const formatDuration = (duration: Milliseconds, options: Options = {}) => {
  const { hideZeroHours, showMilliseconds } = options;
  const totalSeconds = Math.floor(duration / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = Math.floor(duration % 1000);

  // Format each value with at least two digits
  const hoursStr = hideZeroHours && hours === 0 ? '' : hours.toString().padStart(2, '0') + ':';
  const minutesStr = minutes.toString().padStart(2, '0') + ':';
  const secondsStr = seconds.toString().padStart(2, '0');
  const millisecondsStr = showMilliseconds ? `.${milliseconds.toString().padStart(3, '0')}` : '';

  return `${hoursStr}${minutesStr}${secondsStr}${millisecondsStr}`;
};

export const parseFrameCount = (frameCount: string | Frames): Frames | null => {
  if (typeof frameCount === 'string') {
    frameCount = parseInt(frameCount, 10);
  }

  if (isNaN(frameCount)) {
    return null;
  }

  return frameCount;
};

export const getSMPTETimecode = (framesCount: number, frameRate: FrameRate) => {
  const isDropFrame = (frameRate[0] / frameRate[1]) % 1 !== 0;
  return Timecode(framesCount, frameRate, isDropFrame).toString();
};

export const getOverlayWidth = (framesCount: Frames, frameDuration: Milliseconds, scale: number): Pixels => {
  return (
    // make sure the value is not less than the resize handle width, otherwise resize handle will hide and there will be no way to make overlay larger
    Math.max((framesCount * frameDuration * scale) / MILLISECONDS_PER_PIXEL - SEGMENTS_GAP, RESIZE_HANDLE_WIDTH)
  );
};

interface Timeline {
  framesCount: Frames;
  segments: Segment[];
}

type FindAvailableSpaceFn = (segment: Segment, timeline: Timeline, isInitialCall?: boolean) => Segment | null;

/**
 * @description This function returns the position where a given segment can be dropped in case if the target position
 * is occupied or segment is reaches out the timeline boundaries.
 *
 * @param segment – a segment which we want to place on the timeline
 * @param timeline – contains timeline duration and all other segments on the timeline. Other segments are basically
 *                  areas where we can not place the current segment.
 * @param isInitialCall – `findAvailableSpace` function is called recursively. `isInitialCall` param is required to
 *                  add two additional segments on the first call (which serve as boundaries of the timeline, or
 *                  basically areas where current segment can not be placed). Also, `isInitialCall` helps to avoid
 *                  infinite recursion.
 */

export const findAvailableSpace: FindAvailableSpaceFn = ({ start, end }, timeline, isInitialCall = true) => {
  const duration: Frames = end - start;

  if (isInitialCall) {
    // insert starting and ending segment that represent timeline boundaries
    timeline = {
      ...timeline,
      segments: [
        {
          start: -Infinity,
          end: 0
        },
        ...timeline.segments,
        {
          start: timeline.framesCount,
          end: Infinity
        }
      ]
    };
  }

  for (const existingSegment of timeline.segments) {
    const existingStart = existingSegment.start;
    const existingEnd = existingSegment.end;

    if (start >= existingStart && start < existingEnd) {
      // if new segment starts inside an existing segment check if it will fit after existing segment
      return isInitialCall ? findAvailableSpace({ start: Math.max(existingEnd, 0), end: existingEnd + duration }, timeline, false) : null;
    } else if (end > existingStart && end <= existingEnd) {
      // If new segment ends inside an existing segment check if it will fit before existing segment
      return isInitialCall
        ? findAvailableSpace({ start: existingStart - duration, end: Math.min(existingStart, timeline.framesCount) }, timeline, false)
        : null;
    } else if (start <= existingStart && end >= existingEnd) {
      return isInitialCall
        ? findAvailableSpace({ start: Math.max(existingEnd, 0), end: existingEnd + duration }, timeline, false) ||
            findAvailableSpace({ start: existingStart - duration, end: Math.min(existingStart, timeline.framesCount) }, timeline, false)
        : null;
    }
  }

  return { start, end };
};

export const getOptionColor = (kind: SEGMENT_KIND | OVERLAY_KIND) => {
  switch (kind) {
    case OVERLAY_KIND.RATING_CARD:
      return '#D082FF';
    case OVERLAY_KIND.HASHTAG:
      return '#9E54CC';
    case OVERLAY_KIND.LOWER_THIRD:
      return '#EDCFFF';
    case SEGMENT_KIND.BRAND_ID:
      return '#FFD524';
    case SEGMENT_KIND.PROMO:
      return '#46D4CD';
    case SEGMENT_KIND.UNDERWRITING:
      return '#D68D8E';
    case SEGMENT_KIND.CREDITS:
    case SEGMENT_KIND.CREDITS_WITH_WEBTAG_AND_OFFER:
    case SEGMENT_KIND.CREDITS_WITH_WEBTAG:
    case SEGMENT_KIND.CREDITS_WITH_OFFER:
      return '#4395FD';
    case SEGMENT_KIND.WEBTAG:
      return '#9BD4D1';
    case SEGMENT_KIND.OFFER:
      return '#BDC3F2';
    case SEGMENT_KIND.SYSTEM_CUE:
      return '#A7CDFD';
    case SEGMENT_KIND.CONTENT_FILLER:
      return '#FF1493';
    case SEGMENT_KIND.FILLER:
      return '#FFF7D6';
    case SEGMENT_KIND.PLEDGE_BREAK:
      return '#FFE470';
    case SEGMENT_KIND.BLACK:
      return '#BBBFD1';
    case SEGMENT_KIND.ADVISORY:
      return '#D66D6E';
    case SEGMENT_KIND.NEXT_TIME_TEASE:
      return '#98CD98';
    case SEGMENT_KIND.NEXT_TIME_TEASE_WITH_CREDITS:
      return '#5CCD5A';
    default:
      return '#5C6DF2';
  }
};

/**
 * Finds the next overlay in the given layer that is positioned after the specified overlay.
 *
 * @param {OverlayItem[]} layer - An array of overlays contained in a specific layer.
 * @param {OverlayItem} overlay - The reference overlay to find the next overlay relative to.
 * @return {OverlayItem | null} The next overlay in the layer based on position, or null if none is found.
 */
export const findNextOverlayInLayer = (layer: OverlayItem[], overlay: OverlayItem): OverlayItem | null => {
  if (layer.length === 1) {
    // if there is only one overlay in the layer then there can not be "next" overlay
    return null;
  }

  return layer.reduce<OverlayItem | null>((acc, cur) => {
    // this overlay can not be the nextOverlayRef
    if (cur.draggableId === overlay.draggableId) {
      return acc;
    }

    if (!acc) {
      if (cur.start > overlay.start) {
        return cur;
      }
      return acc;
    }

    if (cur.start < acc.start && cur.start > overlay.start) {
      return cur;
    }

    return acc;
  }, null);
};

interface TargetOverlay {
  draggableId?: DraggableId; // if overlay already added to the timeline we need to pass its id
  start: number;
  end: number;
}

export interface IsOverlayFitResult {
  fit: boolean; // is overlay fit on target position
  longerThanTimeline: boolean;
  outsideTimeline: boolean;
  overlayOverlap: boolean;
}

/**
 * Check if overlay will fit in the specific position in the timeline.
 *
 * @param {number} timelineFramesCount
 * @param {OverlayLayer} layer - lay against which we want to check if target overlay will fit.
 * @param {TargetOverlay} targetOverlay - the position of the overlay to check for availability.
 */
export const isOverlayFit = (timelineFramesCount: Frames, layer: OverlayLayer, targetOverlay: TargetOverlay): IsOverlayFitResult => {
  const { start: targetStart, end: targetEnd } = targetOverlay;
  const result: IsOverlayFitResult = {
    fit: true,
    longerThanTimeline: false,
    outsideTimeline: false,
    overlayOverlap: false
  };

  if (targetEnd - targetStart > timelineFramesCount) {
    result.fit = false;
    result.longerThanTimeline = true;
  }

  // if outside timeline
  if (!layer || targetStart < 0 || targetEnd > timelineFramesCount) {
    result.fit = false;
    result.outsideTimeline = true;
  }

  // filter out the target overlay if it's id is provided. We don't want to check it against itself.
  const filteredLayer = layer.filter((o) => {
    if (typeof targetOverlay.draggableId === 'undefined') return true;
    return o.draggableId !== targetOverlay.draggableId;
  });

  for (const overlay of filteredLayer) {
    if (
      // if target start position is inside another overlay
      (targetStart >= overlay.start && targetStart < overlay.end) ||
      // if target end position is inside another overlay
      (targetEnd > overlay.start && targetEnd <= overlay.end) ||
      // if another overlay lies completely inside boundaries of the target position
      (targetStart < overlay.start && targetEnd > overlay.end)
    ) {
      result.fit = false;
      result.overlayOverlap = true;
    }
  }

  return result;
};

interface PlayerSeekParams {
  playerApi: PlayerAPI;
  frameIndex: number;
  dispatch?: AppDispatch;
  updateCurrentFrameInStore?: boolean;
}

export const playerSeek = ({ playerApi, frameIndex, dispatch, updateCurrentFrameInStore }: PlayerSeekParams) => {
  playerApi.pause().catch(console.error);
  playerApi
    .seek({
      format: TimeFormat.Frame,
      mode: SeekMode.Absolute,
      time: frameIndex
    })
    .then(() => {
      if (dispatch && updateCurrentFrameInStore) {
        // We listen for `SmoothTimeUpdateEventType.TimeUpdate` on the `SegmentPlayer`. It fires when the
        // player's time changes due to playback or seeking, and our listener dispatches `setPlayerCurrentFrame`.
        //
        // However, calling `playerApi.seek(...)` sometimes does NOT emit `SmoothTimeUpdateEventType.TimeUpdate` (cause unknown).
        // As a workaround, we update `playerCurrentFrame` in the store immediately after seeking to keep state in sync.
        dispatch(setPlayerCurrentFrame(frameIndex));
      }
    })
    .catch(console.error);
};

export const areAllAssignedSegmentsLoaded = (segments: SegmentItem[]) => {
  const assignedSegments = segments.filter((segment) => segment.source);
  return assignedSegments.every(({ source }) => Boolean(source?.previewUrl || source?.previewLoadingError));
};

export const areAllAssignedItemsPreviewsLoaded = (segments: SegmentItem[], overlays: OverlayItem[]) => {
  const areSegmentPreviewsLoaded = areAllAssignedSegmentsLoaded(segments);
  const assignedOverlays = overlays.filter((overlay) => isAssignedOverlay(overlay));
  const areOverlayPreviewsLoaded = assignedOverlays.every(({ source }) => Boolean(source?.previewUrl || source?.previewLoadingError));

  return areSegmentPreviewsLoaded && areOverlayPreviewsLoaded;
};

export const isBodySegmentKind = (kind: SEGMENT_KIND) => {
  return BODY_SEGMENT_KINDS.includes(kind);
};

export const isOpenTeaseOrCreditsKind = (kind: SEGMENT_KIND) => {
  return (OPEN_TEASE_AND_CREDITS_KINDS as SEGMENT_KIND[]).includes(kind);
};

export const identifyDefaultSegmentDuration = (kind: SEGMENT_KIND): Frames => {
  if (isBodySegmentKind(kind)) {
    return DEFAULT_BODY_SEGMENT_DURATION;
  }
  return DEFAULT_SEGMENT_DURATION;
};

export const BE2FESource = ({
  title_id,
  inventory_id,
  inventory_proxy_id,
  title_name,
  is_screened,
  use_window_start,
  use_window_end,
  external_id
}: SourceBEResponse): Source => ({
  titleId: title_id,
  inventoryId: inventory_id,
  proxyInventoryId: inventory_proxy_id,
  titleName: title_name,
  is_screened,
  use_window_start,
  use_window_end,
  external_id
});

export const FE2BESource = ({ titleId, inventoryId, proxyInventoryId }: Source): SourceBERequest => ({
  title_id: titleId,
  inventory_id: inventoryId,
  inventory_proxy_id: proxyInventoryId
});

const BE2FEOverlayItem = (overlay: OverlayItemBEResponse): OverlayItem => {
  const { kind, label, start, duration, is_locked, source, source_type } = overlay;

  const baseProps: OverlayItemUnassigned = {
    draggableId: nanoid(),
    type: DRAGGABLE_TYPES.OVERLAY,
    kind,
    label,
    start,
    end: start + (duration || DEFAULT_OVERLAY_DURATION),
    isLocked: is_locked
  };

  if (source_type && source) {
    return {
      ...baseProps,
      sourceType: source_type,
      source: BE2FESource(source),
      subtitle: source.title_name
    };
  }

  return baseProps;
};

const BE2FESegmentItem = (segment: SegmentItemBEResponse, prevSegmentEnd?: number): SegmentItem => {
  const { kind, label, duration, start: sourceStart, end: sourceEnd, is_locked, is_super_locked } = segment;
  const start = prevSegmentEnd ?? 0;
  const end = start + (duration === 0 ? identifyDefaultSegmentDuration(kind) : duration || 0);
  const source = segment.source ? BE2FESource(segment.source) : undefined;
  return {
    draggableId: nanoid(),
    type: DRAGGABLE_TYPES.SEGMENT,
    start,
    end,
    kind,
    label,
    isLocked: is_locked,
    isSuperLocked: is_super_locked,
    isIntermediate: false,
    subtitle: segment.source ? segment.source.title_name : undefined,
    source: source ? { ...source, start: sourceStart, end: sourceEnd } : undefined
  };
};

export const FE2BEOverlayItem = (overlay: OverlayItem): OverlayItemBERequest => {
  const { kind, label, start, end, isLocked } = overlay;
  return {
    kind,
    label,
    start,
    duration: end - start,
    is_locked: isLocked,
    source_type: isAssignedOverlay(overlay) ? overlay.sourceType : null,
    source: isAssignedOverlay(overlay) && overlay.source ? FE2BESource(overlay.source) : null
  };
};

export const FE2BESegmentItem = (segment: SegmentItem): SegmentItemBERequest => {
  const { kind, label, start, end, source, isLocked, isSuperLocked } = segment;
  return {
    kind,
    label,
    duration: end - start,
    is_locked: isLocked,
    is_super_locked: isSuperLocked ?? false,
    ...(source ? { source: FE2BESource(source), start: source.start, end: source.end } : { source: null })
  };
};

export const BE2FEAssemblyTemplate = (template: AssemblyTemplateBEResponse): AssemblyTemplate => {
  const overlays: OverlayLayer[] = (template.data?.overlays || []).map((layer) => layer.map(BE2FEOverlayItem));

  let prevSegmentEnd: number | undefined;
  const segments = (template.data?.segments || []).map((segment): SegmentItem => {
    const feSegment = BE2FESegmentItem(segment, prevSegmentEnd);
    prevSegmentEnd = feSegment.end;
    return feSegment;
  });
  return { ...template, data: { overlays, segments } };
};

export const FE2BEAssemblyTemplate = (template: AssemblyTemplate): AssemblyTemplateBERequest => {
  const overlays: OverlayLayerBERequest[] = (template.data?.overlays || []).map((layer) => (layer || []).map(FE2BEOverlayItem));
  const segments: SegmentItemBERequest[] = (template.data?.segments || []).map(FE2BESegmentItem);
  return { ...template, data: { overlays, segments } };
};

export const mapContentAssemblyToTemplateData = (
  assemblyTemplateInputs: AssemblyInputsResponse
): { segments: SegmentItem[]; overlays: OverlayLayer[] } => {
  const segments: SegmentItem[] = [];
  const overlayLayers: OverlayLayer[] = [];

  assemblyTemplateInputs.data.forEach((dataItem) => {
    if (dataItem.type === 'segments') {
      let prevSegmentEnd: number | undefined;
      dataItem.cuts.forEach((segment) => {
        const feSegment = BE2FESegmentItem(segment, prevSegmentEnd);
        prevSegmentEnd = feSegment.end;
        segments.push(feSegment);
      });
      return;
    }

    const overlayLayer: OverlayLayer = [];

    dataItem.cuts.forEach((overlay) => {
      overlayLayer.push(BE2FEOverlayItem(overlay));
    });

    overlayLayers.push(overlayLayer);
  });

  return {
    segments,
    overlays:
      overlayLayers.length === 0
        ? [[]] // Add one empty layer if `overlayLayers` is empty. We want to display at least one layer in the timeline
        : overlayLayers
  };
};

export const preparePackagingForSave = (segments: SegmentItem[], overlays: OverlayLayer[]) => {
  const contents: AssemblyInputsDataRequest = [];

  contents.push({
    type: 'segments',
    cuts: segments.map(FE2BESegmentItem)
  });

  const overlayCuts: AssemblyInputsDataRequest = overlays.map((layer) => ({
    type: 'overlays',
    cuts: layer.map(FE2BEOverlayItem)
  }));

  return contents.concat(overlayCuts);
};
